import React, { useState , useEffect, useCallback, useMemo } from 'react';
import { useHistory } from "react-router-dom";

import { JSONexists, getJSONFromStorage, getCurrentSubscreen, checkPermissions, getDepartmentProgress, getData, updateDepartment, getNextAppScreen, isEmpty } from '../../js/Helpers';
import { buildPeriodsToShow, getPeriodTypeFromScheduleType } from '../../js/PeriodHelpers';
import { submitDepartmentScreen } from '../../js/SaveFunctions';
import { userHasManualPlacementPermission } from '../../js/departmentResourcePermissions';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faLock, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import Dashboard from '../Dashboard';
import Button from '../Button';

import SchoolSetup from './progress/school_setup';
import ScheduleStructureScreen from './progress/schedule_structure_screen';
import UploadData from './progress/upload_data';
import ManageDepartmentsScreen from './progress/manage_departments_screen';
import EditConstraints from './progress/edit_constraints';
import SectionCreation from './progress/section_creation';
import ManageLabels from './progress/manage_labels';
import PlaceSectionsScreen from './progress/place_sections_screen';
import PlaceStudentsScreen from './progress/place_students_screen';

import '../../css/progress.css';

const DEFAULT_PROGRESS_COLORS = ['red', 'orange', 'yellow', 'green', 'turquoise', 'blue', 'bright-blue', 'purple', 'dark-blue-grey'];
const SUPERVISOR_PROGRESS_COLORS = ['blue', 'turquoise', 'purple', 'green', 'bright-blue', 'pastel-dark-blue-grey'];
const ALL_PROGRESS_COLORS = Array.from(new Set([...DEFAULT_PROGRESS_COLORS, ...SUPERVISOR_PROGRESS_COLORS]));

const getDefaultSubscreenForScreen = (screen) => {
	if(screen === 'section_creation') return 'create_sections';
	return null;
};

const normalizeSubscreenForScreen = (screen, passed_subscreen) => {
	const default_subscreen = getDefaultSubscreenForScreen(screen);
	if(default_subscreen && passed_subscreen !== default_subscreen) return default_subscreen;
	if(screen === 'manage_departments' && (!passed_subscreen || passed_subscreen === 'manage_departments')) return 'add_supervisors';
	if(screen === 'place_sections' && passed_subscreen === 'placement_permissions') return 'manual_placements';
	return passed_subscreen;
};

const PROGRESS_STEP_LABELS = {
	school_info: 'School Info',
	school_settings: 'School Settings',
	schedule_type: 'Schedule Type',
	schedule_structure: 'Schedule Structure',
	lunch_structure: 'Lunch Structure',
	upload_sections: 'Upload Sections',
	upload_courses: 'Upload Courses',
	upload_teachers: 'Upload Teachers',
	upload_previous_sections: 'Upload Previous Sections',
	upload_students: 'Upload Students',
	upload_student_requests: 'Upload Student Requests',
	assign_departments: 'Manage Departments',
	add_supervisors: 'Departments & Supervisors',
	manage_departments: 'Departments & Supervisors',
	manage_permissions: 'Manage Permissions',
	supervisor_permissions: 'Supervisor Permissions',
	clean_duplicates: 'Clean Duplicates',
	teacher_availability: 'Teacher Availability',
	course_availability: 'Course Availability',
	course_caps: 'Course Caps',
	course_lengths: 'Course Lengths',
	course_flags: 'Course Flags',
	course_teachers: 'Course Teachers',
	classroom_settings: 'Classroom Settings',
	course_classrooms: 'Course Classrooms',
	teacher_classrooms: 'Teacher Classrooms',
	create_sections: 'Create Sections',
	create_labels: 'Create Labels',
	student_labels: 'Student Labels',
	section_labels: 'Section Labels',
	placement_permissions: 'Placement Permissions',
	manual_placements: 'Manual Placements',
	place_sections: 'Place Sections',
	place_students: 'Place Students',
};

const getProgressStepLabel = (step) => PROGRESS_STEP_LABELS[step?.subscreen] || PROGRESS_STEP_LABELS[step?.screen] || 'Next Step';

const isProgressStepCompleteForUser = (step = {}, userInfo = {}) => (
	!!step.complete || (`${userInfo?.role_id}` === '3' && !!step.awaiting_confirmation)
);

export default function ProgressHSMS({ match, isAuth, authComplete, setShowSwitchingYearModal, schoolSubscribed, rolePages, setRolePages, schoolProgressLoaded, schoolInfo, schoolInfoLoading, setSchoolInfoLoading, setSchoolInfo, schoolType, scheduleInfo, setScheduleInfo, scheduleInfoLoading, scheduleStructureData, setScheduleStructureData, scheduleStructureDataLoading, users, userInfo, usersLoading, appSubscreens, setNotifications, loadScreen, setLoadScreen, departments: appDepartments = [], departmentsLoading: appDepartmentsLoading = true, teachersLoading, setTeachersLoading, teachers, setTeachers, coursesLoading, setCoursesLoading, courses, setCourses, classroomsLoading, setClassroomsLoading, classrooms, setClassrooms, studentsLoading, setStudentsLoading, students, setStudents, studentMatchPercentages, sectionsLoading, setSectionsLoading, sections, setSections, subsections, setSubsections, lunchInfo, labels, setLabels, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo, handlePullNewData }) {
	const history = useHistory();
	
	const [isLoading, setIsLoading] = useState(true);
	const [permissionChecked, setPermissionChecked] = useState(false);
	const [currentSubscreenSet, setCurrentSubscreenSet] = useState(false);

	const [supervisors, setSupervisors] = useState([]);
	const [userIsSupervisor, setUserIsSupervisor] = useState(false);
	const [subscreenResponsibleRoleID, setSubscreenResponsibleRoleID] = useState('1');
	const [subscreenLocked, setSubscreenLocked] = useState(true);
	const [subscreenRestricted, setSubscreenRestricted]= useState(true);
	
	const [scheduleType, setScheduleType] = useState(null);
	const [periods, setPeriods] = useState([]);

	const [subscreen, setSubscreen] = useState(null);
	const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
		try
		{
			const shared_collapsed_value = localStorage.getItem('progress_sidebar_collapsed');
			if(shared_collapsed_value !== null) return shared_collapsed_value === '1';
			const hsms_collapsed_value = localStorage.getItem('hsms_progress_sidebar_collapsed');
			if(hsms_collapsed_value !== null) return hsms_collapsed_value === '1';
			return localStorage.getItem('elementary_progress_sidebar_collapsed') === '1';
		}
		catch(error)
		{
			return false;
		}
	});
	const [departmentID, setDepartmentID] = useState(null);
	const [departmentsLoading, setDepartmentsLoading] = useState(true);
	const [departments, setDepartments] = useState([]);
	const [reviewing, setReviewing] = useState(false);
	const [changesSavedMessage, setChangesSavedMessage] = useState(false);
	const [showNextButton, setShowNextButton] = useState(false);
	const [nextStepLoading, setNextStepLoading] = useState(false);
	const [departmentCompletionPrompt, setDepartmentCompletionPrompt] = useState(null);
	const colors = (`${userInfo.role_id}` === '3') ? SUPERVISOR_PROGRESS_COLORS : DEFAULT_PROGRESS_COLORS;
	const getProgressStepColor = (index) => colors[index % colors.length] || DEFAULT_PROGRESS_COLORS[0];

	const goToScreen = (screen) => {
		sessionStorage.clear();
		setDepartmentCompletionPrompt(null);
		changeDepartment(null);
		setPermissionChecked(false);
		setSubscreen(null);
		setScreenAllowsUniversalSidebar(false);
		setUniversalSidebarLoadingInfo(null);
		setIsLoading(true);

		setLoadScreen('progress');
		history.push(`/app/${schoolType}/progress/${screen}/`);

		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, screen:screen};
		getCurrentSubscreen(data, (temp_subscreen) => {
			const next_subscreen = normalizeSubscreenForScreen(screen, temp_subscreen);
			setSubscreen(next_subscreen);
		});
	}

	const subscreenSelect = (selected_subscreen, selected_screen = null, department_id = null) => {
		const current_screen = (selected_screen) ? selected_screen : match.params.screen;
		const next_subscreen = normalizeSubscreenForScreen(current_screen, selected_subscreen);
		const subscreen_progress_lines = document.querySelectorAll('.progress-page-sidebar-subscreen-progress-line');
		subscreen_progress_lines.forEach(line => line.classList.remove(...ALL_PROGRESS_COLORS, 'green', 'opacity-25'));

		setDepartmentCompletionPrompt(null);
		setIsLoading(true);
		if(department_id)
		{
			setDepartmentID(department_id);
			sessionStorage.setItem(`${current_screen}_department`, department_id);
		}
		else
		{
			changeDepartment(null);
		}
		setPermissionChecked(false);
		setSubscreen(next_subscreen);
		setReviewing(!!department_id);
		setScreenAllowsUniversalSidebar(false);
		setUniversalSidebarLoadingInfo(null);
		
		// Move the user to subscreen but show restricted/locked screen
		history.push(`/app/${schoolType}/progress/${current_screen}/${next_subscreen}`);
		sessionStorage.setItem(`${current_screen}_subscreen`, next_subscreen);
	}

	const handleSubscreenProgressLineHover = (e, color, highlightBool) => {
		const parentDiv = e.currentTarget;
		const line = parentDiv.querySelector('.progress-page-sidebar-subscreen-progress-line');
		if(!line) return;

		const isSelected = parentDiv.classList.contains('progress-page-sidebar-subscreen-btn-selected');
		if(isSelected) return;

		if(highlightBool)
		{
			line.classList.remove(...ALL_PROGRESS_COLORS, 'green');
			line.classList.add(color, 'opacity-25');
		}
		else
		{
			line.classList.remove(...ALL_PROGRESS_COLORS, 'green', 'opacity-25');
		}
	}

	////////////////////////////////
	///// DEPARTMENT FUNCTIONS /////
	////////////////////////////////
	const changeDepartment = useCallback(async (department_id) => {
		if(department_id)
		{
			setDepartmentCompletionPrompt(null);
			setDepartmentID(department_id);
			sessionStorage.setItem(`${match.params.screen}_department`, department_id);
			setReviewing(true);
		}
		else
		{
			sessionStorage.clear();
			setReviewing(false);
			setDepartmentID(null);
		}
	},[match.params.screen])


	const getSubscreenDepartmentProgress = useCallback(async (passed_subscreen, school_id, department_id = null) => {
		setDepartmentsLoading(true);

		getDepartmentProgress({school_id:school_id, schedule_version_id:schoolInfo.current_schedule_version_id, subscreen:passed_subscreen}).then(data => {
			const all_departments = data.departments;
			setDepartments([...all_departments]);
;
			if(department_id) changeDepartment(department_id);

			setDepartmentsLoading(false);
		});
	},[schoolInfo.current_schedule_version_id, changeDepartment])

	const resetProgress = () => {
		// If change is made by non admin, send notifications
		// If screen had been previously submitted, refresh progress
		if(userInfo.role_id === '3')
		{
			getDepartmentProgress({school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, subscreen:subscreen}).then(data => {
				const all_departments = data.departments;
				setDepartments([...all_departments]);
			});
			
			rolePages[`${match.params.screen}`]['subscreens'][subscreen] = {...rolePages[`${match.params.screen}`]['subscreens'][subscreen], ...{current:true, complete:false, awaiting_confirmation:false}};
			setRolePages({...rolePages});
		}
		else 
		{
			const department_index = departments.findIndex(department => department.department_id === departmentID);
			const department_info = departments[department_index];
			const subscreen_info = department_info?.subscreens?.[subscreen] || null;
			
			const current_screen_confirmed = (subscreen_info) ? subscreen_info.confirmed : false;
			
			if(current_screen_confirmed) setChangesSavedMessage(true);
		}
	}

	/////////////////////////////////
	///// SUPERVISORS FUNCTIONS /////
	/////////////////////////////////
	const openSupervisorsList = (department_id = departmentID) => {
		const department_index = departments.findIndex(department => department.department_id === department_id);
		if(department_index !== -1) departments[department_index]['supervisors_open'] = true;
		setDepartments([...departments]);
	}
	
	const closeSupervisorsList = (department_id = departmentID) => {
		const department_index = departments.findIndex(department => department.department_id === department_id);
		if(department_index !== -1) departments[department_index]['supervisors_open'] = false;
		setDepartments([...departments]);
	}
	
	const addSupervisorToDepartment = (supervisor, department_id = departmentID) => {
		const user_id = supervisor.user_id;
		const department_index = departments.findIndex(department => department.department_id === department_id);
		const department_info = departments[department_index];
		const supervisor_index = department_info.supervisors.findIndex(supervisor => supervisor.user_id === user_id);
		
		// If supervisor already exists, dont add them
		if(supervisor_index !== -1) return;

		const saving_supervisor = {...supervisor, supervisor_saving:'add'};
		department_info.supervisors.push(saving_supervisor);
		setDepartments([...departments]);
		
		updateDepartment({update_type:'add_supervisor', school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, department_id:department_id, user_id:user_id}).then(data => {
			const saving_supervisor_index = department_info.supervisors.findIndex(supervisor => supervisor.user_id === user_id);
			if(saving_supervisor_index !== -1) department_info.supervisors[saving_supervisor_index] = supervisor;
			
			const user_index = department_info.supervisors.findIndex(supervisor => supervisor.user_id === userInfo.user_id);
			if(user_index !== -1) setUserIsSupervisor(true);
			
			setDepartments([...departments]);
			closeSupervisorsList(department_id);
		}).catch(error => {
			console.log(error);
			const saving_supervisor_index = department_info.supervisors.findIndex(supervisor => supervisor.user_id === user_id);
			if(saving_supervisor_index !== -1) department_info.supervisors.splice(saving_supervisor_index, 1);
			setDepartments([...departments]);
		});
	}
	
	const removeSupervisorFromDepartment = (user_id, department_id = departmentID) => {
		const department_index = departments.findIndex(department => department.department_id === department_id);
		const department_info = departments[department_index];
		const supervisor_index = department_info.supervisors.findIndex(supervisor => supervisor.user_id === user_id);
		if(supervisor_index === -1 || department_info.supervisors[supervisor_index].supervisor_saving) return;

		department_info.supervisors[supervisor_index] = {...department_info.supervisors[supervisor_index], supervisor_saving:'remove'};
		setDepartments([...departments]);

		updateDepartment({update_type:'remove_supervisor', school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, department_id:department_id, user_id:user_id}).then(data => {
			const supervisor_index = department_info.supervisors.findIndex(supervisor => supervisor.user_id === user_id);
			if(supervisor_index !== -1) department_info.supervisors.splice(supervisor_index, 1);
			
			const user_index = department_info.supervisors.findIndex(supervisor => supervisor.user_id === userInfo.user_id);
			if(user_index === -1 && userInfo.role_id !== '1' && userInfo.role_id !== '2' && userInfo.role_id !== '5') setUserIsSupervisor(false);
			
			closeSupervisorsList(department_id);
			setDepartments([...departments]);
		}).catch(error => {
			console.log(error);
			const supervisor_index = department_info.supervisors.findIndex(supervisor => supervisor.user_id === user_id);
			if(supervisor_index !== -1)
			{
				delete department_info.supervisors[supervisor_index].supervisor_saving;
				setDepartments([...departments]);
			}
		});
	}
	
	const submitDepartment = (department_id = departmentID) => {
		submitDepartmentScreen({school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, department_id:department_id, completed_subscreen:subscreen}).then(all_complete => {
			getData('schoolProgress', '/get-school-progress', {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true).then(user_progress_data => {
				const role_pages = user_progress_data.role_pages;

				setRolePages(role_pages);
			});

			// Get updated department progress
			getDepartmentProgress({school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, subscreen:subscreen}).then(data => {
				const all_departments = data.departments;
				const submitted_department = all_departments.find(department_info => `${department_info.department_id}` === `${department_id}`);

				setDepartments([...all_departments]);
				setDepartmentCompletionPrompt({
					department_id: department_id,
					department_name: submitted_department ? submitted_department.department : 'Department',
					screen: match.params.screen,
					subscreen: subscreen,
				});
				changeDepartment(null);
			});
		});
	}

	const handleMoveToImmediateNextStep = (department_id = null) => {
		if(nextStepLoading) return;

		const destination_step = immediate_next_progress_step;
		setDepartmentCompletionPrompt(null);
		setReviewing(false);
		setScreenAllowsUniversalSidebar(false);
		setUniversalSidebarLoadingInfo(null);

		if(!destination_step)
		{
			sessionStorage.clear();
			setDepartmentID(null);
			setSubscreen(null);
			history.push(`/app/${schoolType}/progress`);
			return;
		}

		sessionStorage.clear();
		setPermissionChecked(false);
		setSubscreen(destination_step.subscreen);
		sessionStorage.setItem(`${destination_step.screen}_subscreen`, destination_step.subscreen);

		if(department_id && `${destination_step.responsible_role_id}` === '3')
		{
			setDepartmentID(department_id);
			setReviewing(true);
			sessionStorage.setItem(`${destination_step.screen}_department`, department_id);
		}
		else
		{
			setDepartmentID(null);
		}

		history.push(`/app/${schoolType}/progress/${destination_step.screen}/${destination_step.subscreen}`);
	}

	const updateRolePagesAfterSubscreenCompletion = useCallback((result, completed_subscreen) => {
		if(!result || !result.subscreen_complete) return;

		setRolePages(current_role_pages => {
			const current_screen = result.current_screen;
			const next_screen = result.next_screen;
			const next_subscreen = result.next_subscreen;
			const current_screen_info = current_role_pages?.[current_screen];
			if(!current_screen_info) return current_role_pages;

			const updated_role_pages = {
				...current_role_pages,
				[current_screen]: {
					...current_screen_info,
					subscreens: current_screen_info.subscreens
				}
			};

			updated_role_pages[current_screen].subscreens = current_screen_info.subscreens.map(subscreen_info => {
				if(subscreen_info.subscreen === completed_subscreen) return {...subscreen_info, complete:true};
				if(subscreen_info.subscreen === next_subscreen) return {...subscreen_info, current:true};
				return subscreen_info;
			});

			if(result.screen_complete && current_screen !== next_screen && updated_role_pages[next_screen])
			{
				updated_role_pages[next_screen] = {
					...updated_role_pages[next_screen],
					subscreens: updated_role_pages[next_screen].subscreens.map(subscreen_info => ({...subscreen_info, current:true}))
				};
			}

			return updated_role_pages;
		});
	}, [setRolePages]);

	const handleMarkCurrentSubscreenComplete = useCallback(() => {
		return getNextAppScreen({
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			completed_subscreen: subscreen,
			callback: result => updateRolePagesAfterSubscreenCompletion(result, subscreen)
		});
	}, [schoolInfo.school_id, schoolInfo.current_schedule_version_id, subscreen, updateRolePagesAfterSubscreenCompletion]);

	const handleMoveToNextScreen = () => {
		if(nextStepLoading) return;
		setNextStepLoading(true);

		getNextAppScreen({school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, completed_subscreen:subscreen, callback: (result) => {
			sessionStorage.clear();
			
			// Update school progress
			const next_screen = result.next_screen;
			const next_subscreen = result.next_subscreen;

			updateRolePagesAfterSubscreenCompletion(result, subscreen);

			const fallback_next_step = next_screen && next_subscreen && rolePages?.[next_screen]?.subscreens?.find(temp_subscreen => temp_subscreen.subscreen === next_subscreen) ? {screen: next_screen, subscreen: next_subscreen} : null;
			const destination_step = immediate_next_progress_step || fallback_next_step;

			setReviewing(false);
			setScreenAllowsUniversalSidebar(false);
			setUniversalSidebarLoadingInfo(null);
			setNextStepLoading(false);

			if(destination_step)
			{
				setSubscreen(destination_step.subscreen);
				sessionStorage.setItem(`${destination_step.screen}_subscreen`, destination_step.subscreen);
				history.push(`/app/${schoolType}/progress/${destination_step.screen}/${destination_step.subscreen}`);
			}
			else
			{
				setSubscreen(null);
				history.push(`/app/${schoolType}/progress`);
			}
		}}).catch(error => {
			console.log(error);
			setNextStepLoading(false);
		});
	}

	//////////////////////////////
	///// DO ON STATE CHANGE /////
	//////////////////////////////
	useEffect(() => {
		// Check if user is logged in
		// If not, send them to log in screen with a redirect to this screen
		// If yes, check if user has permission
		// If not permission, redirect to loading page
		if(authComplete && !isAuth)
		{
			history.push({
				pathname: '/app/login',
				state: {redirect: `/app/${schoolType}/${match.params.screen}`}
			});
		}
		else if(authComplete && loadScreen === 'select_school')
		{
			history.push({
				pathname: '/app/select_school',
				state: {redirect: `/app/${schoolType}/${match.params.screen}`}
			});
		}
		else if(authComplete && schoolProgressLoaded && !permissionChecked && match.params.screen && subscreen && !schoolInfoLoading)
		{
			checkPermissions({school_id:schoolInfo.school_id, subscreen:subscreen, schedule_version_id:schoolInfo.current_schedule_version_id, callback:(result) => {
				history.push(`/app/${schoolType}/progress/${match.params.screen}/${subscreen}`);

				if(result.permissions !== 'locked' && result.permissions !== 'restricted')
				{
					setLoadScreen('progress');
					setSubscreenLocked(false);
					setSubscreenRestricted(false);
	
					setReviewing(false);
					sessionStorage.setItem(`${match.params.screen}_subscreen`, subscreen);
					
				}
				else if(result.permissions === 'restricted')
				{
					setSubscreenRestricted(true);
				}
				else if(result.permissions === 'locked')
				{
					setSubscreenLocked(true);
				}
				
				setIsLoading(false);
				setPermissionChecked(true);
			}});
		}
	}, [isAuth, authComplete, schoolProgressLoaded, subscreen, schoolInfoLoading, loadScreen, match.params.screen, permissionChecked, history, schoolInfo, schoolType, setLoadScreen]);

	useEffect(() => {
		if(!schoolInfoLoading && !currentSubscreenSet)
		{
			if(!match.params.screen)
			{
				sessionStorage.clear();
			}
			else if(match.params.subscreen)
			{
				const next_subscreen = normalizeSubscreenForScreen(match.params.screen, match.params.subscreen);
				setSubscreen(next_subscreen);
			}
			else if(JSONexists(`${match.params.screen}_subscreen`, 'session'))
			{
				const stored_subscreen = getJSONFromStorage(`${match.params.screen}_subscreen`, false, 'session');
				const next_subscreen = normalizeSubscreenForScreen(match.params.screen, stored_subscreen);
				setSubscreen(next_subscreen);
			}
			else
			{
				const data = {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, screen: match.params.screen};

				if(match.params.screen)
				{
					getCurrentSubscreen(data, (temp_subscreen) => {
						const next_subscreen = normalizeSubscreenForScreen(match.params.screen, temp_subscreen);
						setSubscreen(next_subscreen);
					});
				}
			}

			setCurrentSubscreenSet(true);
		}
	}, [schoolInfoLoading, currentSubscreenSet, match.params.screen, match.params.subscreen, schoolInfo, loadScreen, permissionChecked]);

	useEffect(() => {
		if(!usersLoading)
		{
			const supervisors = users.filter(user => user.role_id !== '4');
			setSupervisors([...supervisors]);
		}
	}, [usersLoading, users]);

	useEffect(() => {
		let supervisor_index = -1; 

		if(departmentID && departments.length > 0)
		{
			const department_index = departments.findIndex(department => department.department_id === departmentID);
			const department_info = departments[department_index];
			
			supervisor_index = (department_info) ? department_info.supervisors.findIndex(supervisor => supervisor.user_id === userInfo.user_id) : -1;
		}

		if(supervisor_index !== -1 || userInfo.role_id === '1' || userInfo.role_id === '2' || userInfo.role_id === '5')
		{
			setUserIsSupervisor(true);
		}
		else
		{
			setUserIsSupervisor(false);
		}
	}, [departmentID, departments, userInfo]);
	
	useEffect(() => {
		if(!scheduleInfoLoading && !scheduleStructureDataLoading)
		{
			const schedule_type = scheduleInfo.schedule_type;
			const period_type = getPeriodTypeFromScheduleType(schedule_type);
			const sorted_periods = buildPeriodsToShow(scheduleStructureData, {
				period_type,
				current_day: '1',
				natural_sort_course_periods: true,
			});

			setScheduleType(schedule_type);
			setPeriods([...sorted_periods]);
		}
	}, [scheduleInfoLoading, scheduleStructureDataLoading, scheduleInfo.schedule_type, scheduleStructureData]);
	
	useEffect(() => {
		if(!teachersLoading && !scheduleInfoLoading && !scheduleStructureDataLoading && !departmentsLoading && !usersLoading) setIsLoading(false);
	}, [teachersLoading, scheduleInfoLoading, scheduleStructureDataLoading, departmentsLoading, usersLoading]);

	useEffect(() => {
		if(subscreenLocked || subscreenRestricted)
		{
			setShowNextButton(false);
			return;
		}
		
		if(rolePages && match.params.screen && subscreen && !departmentsLoading)
		{
			const role_page_subscreens = rolePages[`${match.params.screen}`].subscreens;
			const subscreen_index = role_page_subscreens.findIndex(temp_subscreen => temp_subscreen.subscreen === subscreen);
			const responsible_role_id = (subscreen_index !== -1) ? role_page_subscreens[subscreen_index].responsible_role_id : null;
			setSubscreenResponsibleRoleID(responsible_role_id);
			if(!responsible_role_id || responsible_role_id === '2') return;

			const show_next_button = departments.reduce((result, department) => {
				const department_supervisors = Array.isArray(department.supervisors) ? department.supervisors : [];
				const user_is_supervisor = (department_supervisors.findIndex(supervisor => supervisor.user_id === userInfo.user_id) !== -1) ? true : false;

				const department_subscreen = department?.subscreens?.[subscreen];
				if(!department_subscreen) return false;

				const department_submitted = !!department_subscreen.submitted;
				const department_confirmed = !!department_subscreen.confirmed;

				if((userInfo.role_id === '1' || userInfo.role_id === '2' || userInfo.role_id === '5') && !department_confirmed) result = false;
				if(userInfo.role_id === '3' && user_is_supervisor && !department_submitted) result = false;

				return result;
			}, true);

			setShowNextButton(show_next_button);
		}
		
	}, [subscreen, departments, departmentsLoading, rolePages, match.params.screen, subscreenLocked, subscreenRestricted, userInfo]);
	
	/////////////////////
	///// DO ONLOAD /////
	/////////////////////
	useEffect(() => {
		if(!isEmpty(schoolInfo) && !isEmpty(scheduleInfo)) 
		{
			if(subscreen)
			{
				const department_id = (JSONexists(`${match.params.screen}_department`, 'session')) ? getJSONFromStorage(`${match.params.screen}_department`, false, 'session') : null;

				getSubscreenDepartmentProgress(subscreen, schoolInfo.school_id, department_id);
				setShowSwitchingYearModal(false);
			}
		}
	}, [subscreen, schoolInfo, scheduleInfo, getSubscreenDepartmentProgress, match.params.screen, setShowSwitchingYearModal]);

	useEffect(() => {
		try
		{
			localStorage.setItem('progress_sidebar_collapsed', sidebarCollapsed ? '1' : '0');
			localStorage.setItem('hsms_progress_sidebar_collapsed', sidebarCollapsed ? '1' : '0');
		}
		catch(error)
		{
			// Ignore storage failures to avoid disrupting page interactions.
		}
	}, [sidebarCollapsed]);
	
	//console.log(loadScreen);
	//console.log({subscreen, departments, departmentID, users, supervisors, usersLoading});
	//console.log({teachers, students, classrooms, courses});
	//console.log({rolePages});
	//console.log({showNextButton});

	const current_user_is_admin = (userInfo.role_id === '1'|| userInfo.role_id === '2' || userInfo.role_id === '5');
	const manual_placement_permission_resolved = current_user_is_admin || !appDepartmentsLoading;
	const user_has_manual_placement_permission = current_user_is_admin || (!appDepartmentsLoading && userHasManualPlacementPermission(appDepartments, userInfo));
	const user_departments = (current_user_is_admin) ? departments : departments.filter(department_info => {
		const department_supervisors = Array.isArray(department_info.supervisors) ? department_info.supervisors : [];
		return department_supervisors.findIndex(supervisor => supervisor.user_id === userInfo.user_id) !== -1;
	});
	const non_user_departments = (current_user_is_admin) ? [] : departments.filter(department_info => {
		const department_supervisors = Array.isArray(department_info.supervisors) ? department_info.supervisors : [];
		return department_supervisors.findIndex(supervisor => supervisor.user_id === userInfo.user_id) === -1;
	});
	const sorted_departments = [...user_departments, ...non_user_departments];

	const progress_steps = useMemo(() => {
		if(isEmpty(rolePages)) return [];

		return Object.entries(rolePages).reduce((steps, [screen, screen_info]) => {
			const screen_order = parseInt(screen_info.screen_order || 999);
			const subscreens = Array.isArray(screen_info.subscreens) ? screen_info.subscreens : [];

			subscreens.forEach((subscreen_info, subscreen_index) => {
				if(screen === 'place_sections' && subscreen_info.subscreen === 'placement_permissions') return;
				if(screen === 'place_sections' && subscreen_info.subscreen === 'manual_placements' && manual_placement_permission_resolved && !user_has_manual_placement_permission) return;

				steps.push({
					...subscreen_info,
					screen,
					screen_order,
					subscreen_index,
					label: getProgressStepLabel({screen, subscreen: subscreen_info.subscreen}),
				});
			});

			return steps;
		}, []).sort((a, b) => {
			if(a.screen_order !== b.screen_order) return a.screen_order - b.screen_order;
			return a.subscreen_index - b.subscreen_index;
		});
	}, [rolePages, manual_placement_permission_resolved, user_has_manual_placement_permission]);

	const immediate_next_progress_step = useMemo(() => {
		if(progress_steps.length === 0 || !subscreen) return null;

		const current_screen = match.params.screen;
		const current_step_index = progress_steps.findIndex(step => step.screen === current_screen && step.subscreen === subscreen);
		if(current_step_index === -1) return progress_steps[0] || null;

		return progress_steps[current_step_index + 1] || null;
	}, [progress_steps, match.params.screen, subscreen]);

	const nextStepButtonText = immediate_next_progress_step ? `Go to Next Step: ${immediate_next_progress_step.label}` : 'Go to Progress';
	const showDepartmentCompletionPrompt = !!departmentCompletionPrompt && !departmentID && departmentCompletionPrompt.screen === match.params.screen && departmentCompletionPrompt.subscreen === subscreen && `${subscreenResponsibleRoleID}` === '3';
	const completedDepartmentStepLabel = departmentCompletionPrompt ? getProgressStepLabel({subscreen: departmentCompletionPrompt.subscreen}) : '';
	const childShowNextButton = showNextButton && !showDepartmentCompletionPrompt;

	let last_screen = 'school_setup';
	if(!isEmpty(rolePages))
	{
		let min_incomplete_screen_order = 999;

		for (let [temp_screen, temp_screen_info] of Object.entries(rolePages)) {
			const screen_order = temp_screen_info.screen_order;

			if(screen_order > min_incomplete_screen_order) continue;

			const screen_subscreens = temp_screen_info.subscreens;
			const screen_incomplete = screen_subscreens.findIndex(temp_subscreen => !isProgressStepCompleteForUser(temp_subscreen, userInfo)) !== -1;
	
			if(screen_incomplete)
			{
				min_incomplete_screen_order = screen_order;
				last_screen = temp_screen;
			}
		}
	}
	
	let step_index = 0;
	const toggleSidebarCollapsed = () => {
		setSidebarCollapsed((previous_collapsed) => !previous_collapsed);
	};
	const shouldRenderSidebarSubscreens = (screen_key, screen_subscreens = []) => {
		if(screen_subscreens.length <= 1) return false;
		if(sidebarCollapsed) return true;
		return match.params.screen === screen_key;
	};
	const getScreenSubscreens = (screen_key, role_page_subscreens = []) => {
		const allowed_subscreens = new Set(role_page_subscreens.map(subscreen_info => subscreen_info.subscreen));
		return (appSubscreens || []).filter(screen_info => {
			if(screen_info.screen !== screen_key || !allowed_subscreens.has(screen_info.subscreen)) return false;
			if(screen_key === 'place_sections' && screen_info.subscreen === 'placement_permissions') return false;
			if(screen_key === 'place_sections' && screen_info.subscreen === 'manual_placements' && manual_placement_permission_resolved && !user_has_manual_placement_permission) return false;
			return true;
		});
	};
	const getRolePageSubscreen = (role_page_subscreens = [], subscreen_key) => {
		return role_page_subscreens.find(subscreen_info => subscreen_info.subscreen === subscreen_key) || {};
	};
	const renderCollapsedSidebarPopupHeader = (screen_key, step_name, color, complete, current, awaiting_confirmation = false) => {
		if(!sidebarCollapsed) return null;

		return (
			<div className={`progress-page-sidebar-popup-header progress-page-sidebar-popup-header-${color} ${(complete || current) ? 'cursor-pointer' : ''} ${match.params.screen === screen_key ? 'progress-page-sidebar-popup-header-selected' : ''}`} onClick={((complete || current) && match.params.screen !== screen_key) ? (() => goToScreen(screen_key)) : null}>
				<div className='progress-page-sidebar-popup-header-title'>{step_name}</div>
				<div className='progress-step-button-container'>
					{(awaiting_confirmation) ?
						(
							<FontAwesomeIcon className='progress-sidebar-step-check yellow-text' icon={faCheckCircle}/>
						) : (complete) ?
						(
							<FontAwesomeIcon className='progress-sidebar-step-check green-text' icon={faCheckCircle}/>
						) : (current) ?
						(
							<FontAwesomeIcon className='progress-sidebar-step-check mid-gray-text' icon={faCheckCircle}/>
						) :
						(
							<FontAwesomeIcon className='progress-sidebar-step-pending mid-gray-text' icon={faLock}/>
						)
					}
				</div>
			</div>
		);
	};
	
	return (
		<div id='progress-page-main-container' className={`elem-progress-layout ${sidebarCollapsed ? 'elem-progress-sidebar-collapsed' : ''}`}>
			<div id='progress-page-sidebar' className={sidebarCollapsed ? 'elem-progress-sidebar-collapsed' : ''}>
				<button
					type='button'
					className='elem-progress-sidebar-toggle-tab'
					onClick={toggleSidebarCollapsed}
					title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
				>
					<FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft}/>
				</button>
				<div className='elem-progress-sidebar-inner'>
				<div className='secondary-sidebar-header'>Scheduling Steps</div>
				{!rolePages ?
					(
						<div className='center-text'>
							<img src={require('../../images/balls.gif')} alt='Loading...' style={{height:'25px'}}/>
						</div>
					):
					(
						<>
						{('school_setup' in rolePages) &&
							(() => {
								const role_page_subscreens = rolePages['school_setup']['subscreens'];
								const screen_subscreens = getScreenSubscreens('school_setup', role_page_subscreens);

								let color = getProgressStepColor(step_index);

								const current = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.current) !== -1) ? true : false;
								const complete = (role_page_subscreens.findIndex(subscreen_info => !subscreen_info.complete) !== -1) ? false : true;
								
								step_index++;
								
								return (
									<div className='progress-page-sidebar-step-group'>
									<div className={`progress-page-sidebar-btn ${(complete || current) ? 'cursor-pointer' : ''} ${match.params.screen === 'school_setup' ? 'progress-page-sidebar-btn-selected' : ''}`} onClick={((complete || current) && match.params.screen !== 'school_setup') ? (() => goToScreen('school_setup')) : null}>
										<div className={`progress-sidebar-step-number ${color}`}>{step_index}</div>
										<div className='progress-sidebar-step-name'>School Set Up</div>
										<div className='progress-step-button-container'>
											{(complete) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check green-text' icon={faCheckCircle}/>
												) : (current) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check mid-gray-text' icon={faCheckCircle}/>
												) : 
												(
													<FontAwesomeIcon className={`progress-sidebar-step-pending mid-gray-text`} icon={faLock}/>
												)
											}
										</div>
									</div>
									{shouldRenderSidebarSubscreens('school_setup', screen_subscreens) &&
										<div className='progress-page-sidebar-subscreens-container'>
											{renderCollapsedSidebarPopupHeader('school_setup', 'School Set Up', color, complete, current)}
											{screen_subscreens.map((screen_info, i) => {
												const temp_subscreen = screen_info.subscreen;
												const subscreen_name = temp_subscreen.replaceAll('_', ' ').toLowerCase().split(' ').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' ');

												const role_page_subscreen = getRolePageSubscreen(role_page_subscreens, temp_subscreen);
												const subscreen_complete = role_page_subscreen.complete;
												const subscreen_current = role_page_subscreen.current;
												const subscreen_awaiting_confirmation = role_page_subscreen.awaiting_confirmation;

												return (
													<div className={`progress-page-sidebar-subscreen-btn ${match.params.subscreen === temp_subscreen ? 'progress-page-sidebar-subscreen-btn-selected' : ''}`} key={i} onMouseEnter={(e) => handleSubscreenProgressLineHover(e, color, true)}
												onMouseLeave={(e) => handleSubscreenProgressLineHover(e, color, false)} onClick={(match.params.screen !== 'school_setup' || match.params.subscreen !== temp_subscreen) ? () => subscreenSelect(temp_subscreen, 'school_setup') : null}>
														<div className={`progress-page-sidebar-subscreen-progress-line ${match.params.subscreen === temp_subscreen ? color : ''}`}></div>
													<div className='progress-page-sidebar-subscreen-name'>{subscreen_name}</div>
														{(subscreen_awaiting_confirmation) ? 
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check yellow-text' icon={faCheckCircle}/>
															) : (subscreen_complete) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check green-text' icon={faCheckCircle}/>
															) : (subscreen_current) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check mid-gray-text' icon={faCheckCircle}/>
															) : 
															(
																<FontAwesomeIcon className={`progress-page-sidebar-subscreen-step-pending mid-gray-text`} icon={faLock}/>
															)
														}
													</div>
												)
											})}
										</div>
									}
									</div>
								)
							})()
						}
						{('schedule_structure' in rolePages) &&
							(() => {
								const role_page_subscreens = rolePages['schedule_structure']['subscreens'];
								const screen_subscreens = getScreenSubscreens('schedule_structure', role_page_subscreens);

								let color = getProgressStepColor(step_index);

								const current = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.current) !== -1) ? true : false;
								const complete = (role_page_subscreens.findIndex(subscreen_info => !subscreen_info.complete) !== -1) ? false : true;

								step_index++;
								
								return (
									<div className='progress-page-sidebar-step-group'>
									<div className={`progress-page-sidebar-btn ${(complete || current) ? 'cursor-pointer' : ''} ${match.params.screen === 'schedule_structure' ? 'progress-page-sidebar-btn-selected' : ''}`} onClick={((complete || current) && match.params.screen !== 'schedule_structure')  ? (() => goToScreen('schedule_structure')) : null}>
										<div className={`progress-sidebar-step-number ${color}`}>{step_index}</div>
										<div className='progress-sidebar-step-name'>Schedule Structure</div>
										<div className='progress-step-button-container'>
											{(complete) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check green-text' icon={faCheckCircle}/>
												) : (current) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check mid-gray-text' icon={faCheckCircle}/>
												) : 
												(
													<FontAwesomeIcon className={`progress-sidebar-step-pending mid-gray-text`} icon={faLock}/>
												)
											}
										</div>
									</div>
									{shouldRenderSidebarSubscreens('schedule_structure', screen_subscreens) &&
										<div className='progress-page-sidebar-subscreens-container'>
											{renderCollapsedSidebarPopupHeader('schedule_structure', 'Schedule Structure', color, complete, current)}
											{screen_subscreens.map((screen_info, i) => {
												const temp_subscreen = screen_info.subscreen;
												const subscreen_name = temp_subscreen.replaceAll('_', ' ').toLowerCase().split(' ').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' ');

												const role_page_subscreen = getRolePageSubscreen(role_page_subscreens, temp_subscreen);
												const subscreen_complete = role_page_subscreen.complete;
												const subscreen_current = role_page_subscreen.current;
												const subscreen_awaiting_confirmation = role_page_subscreen.awaiting_confirmation;

												return (
													<div className={`progress-page-sidebar-subscreen-btn ${match.params.subscreen === temp_subscreen ? 'progress-page-sidebar-subscreen-btn-selected' : ''}`} key={i} onMouseEnter={(e) => handleSubscreenProgressLineHover(e, color, true)}
												onMouseLeave={(e) => handleSubscreenProgressLineHover(e, color, false)} onClick={(match.params.screen !== 'schedule_structure' || match.params.subscreen !== temp_subscreen) ? () => subscreenSelect(temp_subscreen, 'schedule_structure') : null}>
														<div className={`progress-page-sidebar-subscreen-progress-line ${match.params.subscreen === temp_subscreen ? color : ''}`}></div>
													<div className='progress-page-sidebar-subscreen-name'>{subscreen_name}</div>
														{(subscreen_awaiting_confirmation) ? 
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check yellow-text' icon={faCheckCircle}/>
															) : (subscreen_complete) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check green-text' icon={faCheckCircle}/>
															) : (subscreen_current) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check mid-gray-text' icon={faCheckCircle}/>
															) : 
															(
																<FontAwesomeIcon className={`progress-page-sidebar-subscreen-step-pending mid-gray-text`} icon={faLock}/>
															)
														}
													</div>
												)
											})}
										</div>
									}
									</div>
								)
							})()
						}
						{('upload_data' in rolePages) &&
							(() => {
								const role_page_subscreens = rolePages['upload_data']['subscreens'];
								const screen_subscreens = getScreenSubscreens('upload_data', role_page_subscreens);

								let color = getProgressStepColor(step_index);

								const current = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.current) !== -1) ? true : false;
								const complete = (role_page_subscreens.findIndex(subscreen_info => !subscreen_info.complete) !== -1) ? false : true;

								step_index++;
								
								return (
									<div className='progress-page-sidebar-step-group'>
									<div className={`progress-page-sidebar-btn ${(complete || current) ? 'cursor-pointer' : ''} ${match.params.screen === 'upload_data' ? 'progress-page-sidebar-btn-selected' : ''}`} onClick={((complete || current) && match.params.screen !== 'upload_data') ? (() => goToScreen('upload_data')) : null}>
										<div className={`progress-sidebar-step-number ${color}`}>{step_index}</div>
										<div className='progress-sidebar-step-name'>Upload Data</div>
										<div className='progress-step-button-container'>
											{(complete) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check green-text' icon={faCheckCircle}/>
												) : (current) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check mid-gray-text' icon={faCheckCircle}/>
												) : 
												(
													<FontAwesomeIcon className={`progress-sidebar-step-pending mid-gray-text`} icon={faLock}/>
												)
											}
										</div>
									</div>
									{shouldRenderSidebarSubscreens('upload_data', screen_subscreens) &&
										<div className='progress-page-sidebar-subscreens-container'>
											{renderCollapsedSidebarPopupHeader('upload_data', 'Upload Data', color, complete, current)}
											{screen_subscreens.map((screen_info, i) => {
												const temp_subscreen = screen_info.subscreen;
												const subscreen_name = temp_subscreen.replaceAll('_', ' ').toLowerCase().split(' ').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' ');

												const role_page_subscreen = getRolePageSubscreen(role_page_subscreens, temp_subscreen);
												const subscreen_complete = role_page_subscreen.complete;
												const subscreen_current = role_page_subscreen.current;
												const subscreen_awaiting_confirmation = role_page_subscreen.awaiting_confirmation;

												return (
													<div className={`progress-page-sidebar-subscreen-btn ${match.params.subscreen === temp_subscreen ? 'progress-page-sidebar-subscreen-btn-selected' : ''}`} key={i} onMouseEnter={(e) => handleSubscreenProgressLineHover(e, color, true)}
												onMouseLeave={(e) => handleSubscreenProgressLineHover(e, color, false)} onClick={(match.params.screen !== 'upload_data' || match.params.subscreen !== temp_subscreen) ? () => subscreenSelect(temp_subscreen, 'upload_data') : null}>
														<div className={`progress-page-sidebar-subscreen-progress-line ${match.params.subscreen === temp_subscreen ? color : ''}`}></div>
													<div className='progress-page-sidebar-subscreen-name'>{subscreen_name}</div>
														{(subscreen_awaiting_confirmation) ? 
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check yellow-text' icon={faCheckCircle}/>
															) : (subscreen_complete) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check green-text' icon={faCheckCircle}/>
															) : (subscreen_current) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check mid-gray-text' icon={faCheckCircle}/>
															) : 
															(
																<FontAwesomeIcon className={`progress-page-sidebar-subscreen-step-pending mid-gray-text`} icon={faLock}/>
															)
														}
													</div>
												)
											})}
										</div>
									}
									</div>
								)
							})()
						}
						{('manage_departments' in rolePages) &&
							(() => {
								const role_page_subscreens = rolePages['manage_departments']['subscreens'];
								const screen_subscreens = getScreenSubscreens('manage_departments', role_page_subscreens);

								let color = getProgressStepColor(step_index);

								const current = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.current) !== -1) ? true : false;
								const complete = (role_page_subscreens.findIndex(subscreen_info => !subscreen_info.complete) !== -1) ? false : true;

								step_index++;
								
								return (
									<div className='progress-page-sidebar-step-group'>
									<div className={`progress-page-sidebar-btn ${(complete || current) ? 'cursor-pointer' : ''} ${match.params.screen === 'manage_departments' ? 'progress-page-sidebar-btn-selected' : ''}`} onClick={((complete || current) && match.params.screen !== 'manage_departments') ? (() => goToScreen('manage_departments')) : null}>
										<div className={`progress-sidebar-step-number ${color}`}>{step_index}</div>
										<div className='progress-sidebar-step-name'>Manage Departments</div>
										<div className='progress-step-button-container'>
											{(complete) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check green-text' icon={faCheckCircle}/>
												) : (current) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check mid-gray-text' icon={faCheckCircle}/>
												) : 
												(
													<FontAwesomeIcon className={`progress-sidebar-step-pending mid-gray-text`} icon={faLock}/>
												)
											}
										</div>
									</div>
									{shouldRenderSidebarSubscreens('manage_departments', screen_subscreens) &&
										<div className='progress-page-sidebar-subscreens-container'>
											{renderCollapsedSidebarPopupHeader('manage_departments', 'Manage Departments', color, complete, current)}
											{screen_subscreens.map((screen_info, i) => {
												const temp_subscreen = screen_info.subscreen;
												const subscreen_name = getProgressStepLabel({screen:'manage_departments', subscreen: temp_subscreen});

												const role_page_subscreen = getRolePageSubscreen(role_page_subscreens, temp_subscreen);
												const subscreen_complete = role_page_subscreen.complete;
												const subscreen_current = role_page_subscreen.current;
												const subscreen_awaiting_confirmation = role_page_subscreen.awaiting_confirmation;

												return (
													<div className={`progress-page-sidebar-subscreen-btn ${match.params.subscreen === temp_subscreen ? 'progress-page-sidebar-subscreen-btn-selected' : ''}`} key={i} onMouseEnter={(e) => handleSubscreenProgressLineHover(e, color, true)}
												onMouseLeave={(e) => handleSubscreenProgressLineHover(e, color, false)} onClick={(match.params.screen !== 'manage_departments' || match.params.subscreen !== temp_subscreen) ? () => subscreenSelect(temp_subscreen, 'manage_departments') : null}>
														<div className={`progress-page-sidebar-subscreen-progress-line ${match.params.subscreen === temp_subscreen ? color : ''}`}></div>
													<div className='progress-page-sidebar-subscreen-name'>{subscreen_name}</div>
														{(subscreen_awaiting_confirmation) ? 
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check yellow-text' icon={faCheckCircle}/>
															) : (subscreen_complete) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check green-text' icon={faCheckCircle}/>
															) : (subscreen_current) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check mid-gray-text' icon={faCheckCircle}/>
															) : 
															(
																<FontAwesomeIcon className={`progress-page-sidebar-subscreen-step-pending mid-gray-text`} icon={faLock}/>
															)
														}
													</div>
												)
											})}
										</div>
									}
									</div>
								)
							})()
						}
						{('edit_constraints' in rolePages) &&
							(() => {
								const role_page_subscreens = rolePages['edit_constraints']['subscreens'];
								const screen_subscreens = getScreenSubscreens('edit_constraints', role_page_subscreens);

								let color = getProgressStepColor(step_index);

								const current = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.current) !== -1) ? true : false;
								const complete = (role_page_subscreens.findIndex(subscreen_info => !subscreen_info.complete) !== -1) ? false : true;
								const awaiting_confirmation = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.awaiting_confirmation) !== -1) ? true : false;

								step_index++;
								
								return (
									<div className='progress-page-sidebar-step-group'>
									<div className={`progress-page-sidebar-btn ${(complete || current) ? 'cursor-pointer' : ''} ${match.params.screen === 'edit_constraints' ? 'progress-page-sidebar-btn-selected' : ''}`} onClick={((complete || current) && match.params.screen !== 'edit_constraints') ? (() => goToScreen('edit_constraints')) : null}>
										<div className={`progress-sidebar-step-number ${color}`}>{step_index}</div>
										<div className='progress-sidebar-step-name'>Edit Constraints</div>
										<div className='progress-step-button-container'>
											{(awaiting_confirmation) ? 
												(
													<FontAwesomeIcon className='progress-sidebar-step-check yellow-text' icon={faCheckCircle}/>
												) : (complete) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check green-text' icon={faCheckCircle}/>
												) : (current) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check mid-gray-text' icon={faCheckCircle}/>
												) : 
												(
													<FontAwesomeIcon className={`progress-sidebar-step-pending mid-gray-text`} icon={faLock}/>
												)
											}
										</div>
									</div>
									{shouldRenderSidebarSubscreens('edit_constraints', screen_subscreens) &&
										<div className='progress-page-sidebar-subscreens-container'>
											{renderCollapsedSidebarPopupHeader('edit_constraints', 'Edit Constraints', color, complete, current, awaiting_confirmation)}
											{screen_subscreens.map((screen_info, i) => {
												const temp_subscreen = screen_info.subscreen;
												const subscreen_name = temp_subscreen.replaceAll('_', ' ').toLowerCase().split(' ').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' ');

												const role_page_subscreen = getRolePageSubscreen(role_page_subscreens, temp_subscreen);
												const subscreen_complete = role_page_subscreen.complete;
												const subscreen_current = role_page_subscreen.current;
												const subscreen_awaiting_confirmation = role_page_subscreen.awaiting_confirmation;

												return (
													<React.Fragment key={i}>
													<div className={`progress-page-sidebar-subscreen-btn ${match.params.subscreen === temp_subscreen ? 'progress-page-sidebar-subscreen-btn-selected' : ''}`} onMouseEnter={(e) => handleSubscreenProgressLineHover(e, color, true)}
												onMouseLeave={(e) => handleSubscreenProgressLineHover(e, color, false)} onClick={(match.params.screen !== 'edit_constraints' || match.params.subscreen !== temp_subscreen) ? () => subscreenSelect(temp_subscreen, 'edit_constraints') : null}>
														<div className={`progress-page-sidebar-subscreen-progress-line ${match.params.subscreen === temp_subscreen ? color : ''}`}></div>
													<div className='progress-page-sidebar-subscreen-name'>{subscreen_name}</div>
														{(subscreen_awaiting_confirmation) ? 
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check yellow-text' icon={faCheckCircle}/>
															) : (subscreen_complete) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check green-text' icon={faCheckCircle}/>
															) : (subscreen_current) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check mid-gray-text' icon={faCheckCircle}/>
															) : 
															(
																<FontAwesomeIcon className={`progress-page-sidebar-subscreen-step-pending mid-gray-text`} icon={faLock}/>
															)
														}
													</div>
													
													</React.Fragment>
												)
											})}
										</div>
									}
									</div>
								)
							})()
						}
						{('section_creation' in rolePages) &&
							(() => {
								let color = getProgressStepColor(step_index);

								const role_page_subscreens = rolePages['section_creation']['subscreens'];
								const current = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.current) !== -1) ? true : false;
								const complete = (role_page_subscreens.findIndex(subscreen_info => !subscreen_info.complete) !== -1) ? false : true;
								const awaiting_confirmation = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.awaiting_confirmation) !== -1) ? true : false;

								step_index++;
								
								return (
									<div className='progress-page-sidebar-step-group'>
									<div className={`progress-page-sidebar-btn ${(complete || current) ? 'cursor-pointer' : ''} ${match.params.screen === 'section_creation' ? 'progress-page-sidebar-btn-selected' : ''}`} onClick={((complete || current) && match.params.screen !== 'section_creation') ? (() => goToScreen('section_creation')) : null}>
										<div className={`progress-sidebar-step-number ${color}`}>{step_index}</div>
										<div className='progress-sidebar-step-name'>Create Sections</div>
										<div className='progress-step-button-container'>
											{(awaiting_confirmation) ? 
												(
													<FontAwesomeIcon className='progress-sidebar-step-check yellow-text' icon={faCheckCircle}/>
												) : (complete) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check green-text' icon={faCheckCircle}/>
												) : (current) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check mid-gray-text' icon={faCheckCircle}/>
												) : 
												(
													<FontAwesomeIcon className={`progress-sidebar-step-pending mid-gray-text`} icon={faLock}/>
												)
											}
										</div>
									</div>
									</div>
								)
							})()
						}
						{('manage_labels' in rolePages) &&
							(() => {
								const role_page_subscreens = rolePages['manage_labels']['subscreens'];
								const screen_subscreens = getScreenSubscreens('manage_labels', role_page_subscreens);

								let color = getProgressStepColor(step_index);

								const current = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.current) !== -1) ? true : false;
								const complete = (role_page_subscreens.findIndex(subscreen_info => !subscreen_info.complete) !== -1) ? false : true;
								const awaiting_confirmation = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.awaiting_confirmation) !== -1) ? true : false;

								step_index++;
								
									return (
										<div className='progress-page-sidebar-step-group'>
										<div className={`progress-page-sidebar-btn ${(complete || current) ? 'cursor-pointer' : ''} ${match.params.screen === 'manage_labels' ? 'progress-page-sidebar-btn-selected' : ''}`} onClick={((complete || current) && match.params.screen !== 'manage_labels') ? (() => goToScreen('manage_labels')) : null}>
											<div className={`progress-sidebar-step-number ${color}`}>{step_index}</div>
											<div className='progress-sidebar-step-name'>Manage Teams/Labels</div>
											<div className='progress-step-button-container'>
											{(awaiting_confirmation) ? 
												(
													<FontAwesomeIcon className='progress-sidebar-step-check yellow-text' icon={faCheckCircle}/>
												) : (complete) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check green-text' icon={faCheckCircle}/>
												) : (current) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check mid-gray-text' icon={faCheckCircle}/>
												) : 
												(
													<FontAwesomeIcon className={`progress-sidebar-step-pending mid-gray-text`} icon={faLock}/>
												)
											}
										</div>
									</div>
									{shouldRenderSidebarSubscreens('manage_labels', screen_subscreens) &&
											<div className='progress-page-sidebar-subscreens-container'>
												{renderCollapsedSidebarPopupHeader('manage_labels', 'Manage Teams/Labels', color, complete, current, awaiting_confirmation)}
												{screen_subscreens.map((screen_info, i) => {
													const temp_subscreen = screen_info.subscreen;
													const subscreen_name = temp_subscreen.replaceAll('_', ' ').toLowerCase().split(' ').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' ').replace(/\bLabels\b/g, 'Teams/Labels');

												const role_page_subscreen = getRolePageSubscreen(role_page_subscreens, temp_subscreen);
												const subscreen_complete = role_page_subscreen.complete;
												const subscreen_current = role_page_subscreen.current;
												const subscreen_awaiting_confirmation = role_page_subscreen.awaiting_confirmation;

												return (
													<React.Fragment key={i} >
														<div className={`progress-page-sidebar-subscreen-btn ${match.params.subscreen === temp_subscreen ? 'progress-page-sidebar-subscreen-btn-selected' : ''}`} onMouseEnter={(e) => handleSubscreenProgressLineHover(e, color, true)}
												onMouseLeave={(e) => handleSubscreenProgressLineHover(e, color, false)} onClick={(match.params.screen !== 'manage_labels' || match.params.subscreen !== temp_subscreen) ? () => subscreenSelect(temp_subscreen, 'manage_labels') : null}>
															<div className={`progress-page-sidebar-subscreen-progress-line ${match.params.subscreen === temp_subscreen ? color : ''}`}></div>
													<div className='progress-page-sidebar-subscreen-name'>{subscreen_name}</div>
															{(subscreen_awaiting_confirmation) ? 
																(
																	<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check yellow-text' icon={faCheckCircle}/>
																) : (subscreen_complete) ?
																(
																	<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check green-text' icon={faCheckCircle}/>
																) : (subscreen_current) ?
																(
																	<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check mid-gray-text' icon={faCheckCircle}/>
																) : 
																(
																	<FontAwesomeIcon className={`progress-page-sidebar-subscreen-step-pending mid-gray-text`} icon={faLock}/>
																)
															}
														</div>
													</React.Fragment>
												)
											})}
										</div>
									}
										</div>
									)
							})()
						}
						{('place_sections' in rolePages) &&
							(() => {
								const placement_sidebar_pending = !current_user_is_admin && !manual_placement_permission_resolved;
								let color = getProgressStepColor(step_index);

								if(placement_sidebar_pending)
								{
									step_index++;

									return (
										<div className='progress-page-sidebar-step-group'>
											<div className='progress-page-sidebar-btn progress-page-sidebar-btn-neutral'>
												<div className={`progress-sidebar-step-number ${color}`}>{step_index}</div>
												<div className='progress-sidebar-step-name progress-sidebar-step-name-placeholder' aria-label='Loading placement navigation'></div>
												<div className='progress-step-button-container'></div>
											</div>
										</div>
									);
								}

								const role_page_subscreens = (rolePages['place_sections']['subscreens'] || []).filter(subscreen_info => {
									if(subscreen_info.subscreen === 'placement_permissions') return false;
									if(subscreen_info.subscreen === 'manual_placements' && manual_placement_permission_resolved && !user_has_manual_placement_permission) return false;
									return true;
								});
								const screen_subscreens = getScreenSubscreens('place_sections', role_page_subscreens);
								const supervisor_only_reviews_placement = !current_user_is_admin && manual_placement_permission_resolved && screen_subscreens.length > 0 && screen_subscreens.every(screen_info => screen_info.subscreen === 'place_sections');
								const place_sections_sidebar_label = supervisor_only_reviews_placement ? 'Placement Review' : 'Place Sections';
								const handlePlaceSectionsSidebarClick = () => {
									if(supervisor_only_reviews_placement)
									{
										subscreenSelect('place_sections', 'place_sections');
										return;
									}

									goToScreen('place_sections');
								};

								const current = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.current) !== -1) ? true : false;
								const complete = (role_page_subscreens.findIndex(subscreen_info => !subscreen_info.complete) !== -1) ? false : true;
								const awaiting_confirmation = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.awaiting_confirmation) !== -1) ? true : false;

								step_index++;
								
								return (
									<div className='progress-page-sidebar-step-group'>
									<div className={`progress-page-sidebar-btn ${(complete || current) ? 'cursor-pointer' : ''} ${match.params.screen === 'place_sections' ? 'progress-page-sidebar-btn-selected' : ''}`} onClick={((complete || current) && (match.params.screen !== 'place_sections' || (supervisor_only_reviews_placement && match.params.subscreen !== 'place_sections'))) ? handlePlaceSectionsSidebarClick : null}>
										<div className={`progress-sidebar-step-number ${color}`}>{step_index}</div>
										<div className='progress-sidebar-step-name'>{place_sections_sidebar_label}</div>
										<div className='progress-step-button-container'>
											{(awaiting_confirmation) ? 
												(
													<FontAwesomeIcon className='progress-sidebar-step-check yellow-text' icon={faCheckCircle}/>
												) : (complete) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check green-text' icon={faCheckCircle}/>
												) : (current) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check mid-gray-text' icon={faCheckCircle}/>
												) : 
												(
													<FontAwesomeIcon className={`progress-sidebar-step-pending mid-gray-text`} icon={faLock}/>
												)
											}
										</div>
									</div>
									{shouldRenderSidebarSubscreens('place_sections', screen_subscreens) &&
										<div className='progress-page-sidebar-subscreens-container'>
											{renderCollapsedSidebarPopupHeader('place_sections', place_sections_sidebar_label, color, complete, current, awaiting_confirmation)}
											{screen_subscreens.map((screen_info, i) => {
												const temp_subscreen = screen_info.subscreen;
												const subscreen_name = !current_user_is_admin && temp_subscreen === 'place_sections' ? 'Placement Review' : temp_subscreen.replaceAll('_', ' ').toLowerCase().split(' ').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' ');

												const role_page_subscreen = getRolePageSubscreen(role_page_subscreens, temp_subscreen);
												const subscreen_complete = role_page_subscreen.complete;
												const subscreen_current = role_page_subscreen.current;
												const subscreen_awaiting_confirmation = role_page_subscreen.awaiting_confirmation;

												return (
													<React.Fragment key={i} >
														<div className={`progress-page-sidebar-subscreen-btn ${match.params.subscreen === temp_subscreen ? 'progress-page-sidebar-subscreen-btn-selected' : ''}`} onMouseEnter={(e) => handleSubscreenProgressLineHover(e, color, true)}
												onMouseLeave={(e) => handleSubscreenProgressLineHover(e, color, false)} onClick={(match.params.screen !== 'place_sections' || match.params.subscreen !== temp_subscreen) ? () => subscreenSelect(temp_subscreen, 'place_sections') : null}>
															<div className={`progress-page-sidebar-subscreen-progress-line ${match.params.subscreen === temp_subscreen ? color : ''}`}></div>
													<div className='progress-page-sidebar-subscreen-name'>{subscreen_name}</div>
															{(subscreen_awaiting_confirmation) ? 
																(
																	<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check yellow-text' icon={faCheckCircle}/>
																) : (subscreen_complete) ?
																(
																	<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check green-text' icon={faCheckCircle}/>
																) : (subscreen_current) ?
																(
																	<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check mid-gray-text' icon={faCheckCircle}/>
																) : 
																(
																	<FontAwesomeIcon className={`progress-page-sidebar-subscreen-step-pending mid-gray-text`} icon={faLock}/>
																)
															}
														</div>
													</React.Fragment>
												)
											})}
										</div>
									}
									</div>
								)
							})()
						}
						{('place_students' in rolePages) &&
							(() => {
								const role_page_subscreens = rolePages['place_students']['subscreens'];
								const screen_subscreens = getScreenSubscreens('place_students', role_page_subscreens);

								let color = getProgressStepColor(step_index);

								const current = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.current) !== -1) ? true : false;
								const complete = (role_page_subscreens.findIndex(subscreen_info => !subscreen_info.complete) !== -1) ? false : true;
								const awaiting_confirmation = (role_page_subscreens.findIndex(subscreen_info => subscreen_info.awaiting_confirmation) !== -1) ? true : false;

								step_index++;
								
								return (
									<div className='progress-page-sidebar-step-group'>
									<div className={`progress-page-sidebar-btn ${(complete || current) ? 'cursor-pointer' : ''} ${match.params.screen === 'place_students' ? 'progress-page-sidebar-btn-selected' : ''}`} onClick={((complete || current) && match.params.screen !== 'place_students') ? (() => goToScreen('place_students')) : null}>
										<div className={`progress-sidebar-step-number ${color}`}>{step_index}</div>
										<div className='progress-sidebar-step-name'>Place Students</div>
										<div className='progress-step-button-container'>
											{(awaiting_confirmation) ? 
												(
													<FontAwesomeIcon className='progress-sidebar-step-check yellow-text' icon={faCheckCircle}/>
												) : (complete) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check green-text' icon={faCheckCircle}/>
												) : (current) ?
												(
													<FontAwesomeIcon className='progress-sidebar-step-check mid-gray-text' icon={faCheckCircle}/>
												) : 
												(
													<FontAwesomeIcon className={`progress-sidebar-step-pending mid-gray-text`} icon={faLock}/>
												)
											}
										</div>
									</div>
									{shouldRenderSidebarSubscreens('place_students', screen_subscreens) &&
										<div className='progress-page-sidebar-subscreens-container'>
											{renderCollapsedSidebarPopupHeader('place_students', 'Place Students', color, complete, current, awaiting_confirmation)}
											{screen_subscreens.map((screen_info, i) => {
												const temp_subscreen = screen_info.subscreen;
												const subscreen_name = temp_subscreen.replaceAll('_', ' ').toLowerCase().split(' ').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' ');

												const role_page_subscreen = getRolePageSubscreen(role_page_subscreens, temp_subscreen);
												const subscreen_complete = role_page_subscreen.complete;
												const subscreen_current = role_page_subscreen.current;
												const subscreen_awaiting_confirmation = role_page_subscreen.awaiting_confirmation;

												return (
													<div className={`progress-page-sidebar-subscreen-btn ${match.params.subscreen === temp_subscreen ? 'progress-page-sidebar-subscreen-btn-selected' : ''}`} key={i} onMouseEnter={(e) => handleSubscreenProgressLineHover(e, color, true)}
												onMouseLeave={(e) => handleSubscreenProgressLineHover(e, color, false)} onClick={(match.params.screen !== 'place_students' || match.params.subscreen !== temp_subscreen) ? () => subscreenSelect(temp_subscreen, 'place_students') : null}>
														<div className={`progress-page-sidebar-subscreen-progress-line ${match.params.subscreen === temp_subscreen ? color : ''}`}></div>
													<div className='progress-page-sidebar-subscreen-name'>{subscreen_name}</div>
														{(subscreen_awaiting_confirmation) ? 
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check yellow-text' icon={faCheckCircle}/>
															) : (subscreen_complete) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check green-text' icon={faCheckCircle}/>
															) : (subscreen_current) ?
															(
																<FontAwesomeIcon className='progress-page-sidebar-subscreen-step-check mid-gray-text' icon={faCheckCircle}/>
															) : 
															(
																<FontAwesomeIcon className={`progress-page-sidebar-subscreen-step-pending mid-gray-text`} icon={faLock}/>
															)
														}
													</div>
												)
											})}
										</div>
									}
									</div>
								)
							})()
						}
						</>
					)
				}
				</div>
			</div>
			<div id='progress-page-main-content' className={showDepartmentCompletionPrompt ? 'progress-page-main-content-with-bottom-banner' : ''}>
				{showDepartmentCompletionPrompt &&
					<div className='progress-department-next-step-banner'>
						<div>
							<div className='progress-department-next-step-banner-heading'>{departmentCompletionPrompt.department_name} is marked complete for {completedDepartmentStepLabel}.</div>
							<div className='progress-department-next-step-banner-copy'>You can work on another department here, or continue to the next step for this department.</div>
						</div>
						<Button
							classes={['btn', 'btn-extra-large', 'green-btn', 'progress-department-next-step-banner-btn']}
							text={nextStepButtonText}
							onClickFunction={() => handleMoveToImmediateNextStep(departmentCompletionPrompt.department_id)}
						/>
					</div>
				}
				{(() => {
					switch(match.params.screen) {
						case 'school_setup':
							return <SchoolSetup isLoading={isLoading} subscreenRestricted={subscreenRestricted} subscreenLocked={subscreenLocked} subscreen={subscreen} schoolInfo={schoolInfo} setSchoolInfo={setSchoolInfo} scheduleInfo={scheduleInfo} setScheduleInfo={setScheduleInfo} handleMoveToNextScreen={handleMoveToNextScreen}/>;
						case 'schedule_structure':
							return <ScheduleStructureScreen isLoading={isLoading} subscreenRestricted={subscreenRestricted} subscreenLocked={subscreenLocked} schoolProgressLoaded={schoolProgressLoaded} schoolInfo={schoolInfo} schoolInfoLoading={schoolInfoLoading} scheduleInfoLoading={scheduleInfoLoading} scheduleInfo={scheduleInfo} setScheduleInfo={setScheduleInfo} setScheduleStructureData={setScheduleStructureData} subscreen={subscreen} loadScreen={loadScreen} handleMoveToNextScreen={handleMoveToNextScreen}/>;
						case 'upload_data':
							return <UploadData isLoading={isLoading} setIsLoading={setIsLoading} schoolSubscribed={schoolSubscribed} subscreenRestricted={subscreenRestricted} subscreenLocked={subscreenLocked} subscreen={subscreen} setSubscreen={setSubscreen} schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} setScheduleInfo={setScheduleInfo} scheduleStructureData={scheduleStructureData} lunchInfo={lunchInfo} departments={departments} setDepartments={setDepartments} coursesLoading={coursesLoading} setCoursesLoading={setCoursesLoading} courses={courses} setCourses={setCourses} teachersLoading={teachersLoading} setTeachersLoading={setTeachersLoading} teachers={teachers} setTeachers={setTeachers} classroomsLoading={classroomsLoading} setClassroomsLoading={setClassroomsLoading} classrooms={classrooms} setClassrooms={setClassrooms} studentsLoading={studentsLoading} setStudentsLoading={setStudentsLoading} students={students} setStudents={setStudents} sectionsLoading={sectionsLoading} setSectionsLoading={setSectionsLoading} sections={sections} setSections={setSections} setSubsections={setSubsections} labels={labels} setLabels={setLabels} setRolePages={setRolePages} handleMoveToNextScreen={handleMoveToNextScreen}/>;
						case 'manage_departments':
							return <ManageDepartmentsScreen isLoading={isLoading} subscreenRestricted={subscreenRestricted} subscreenLocked={subscreenLocked} subscreen={subscreen} addSupervisorToDepartment={addSupervisorToDepartment} removeSupervisorFromDepartment={removeSupervisorFromDepartment} openSupervisorsList={openSupervisorsList} closeSupervisorsList={closeSupervisorsList} scheduleStructureDataLoading={scheduleStructureDataLoading} departments={departments} setDepartments={setDepartments} supervisors={supervisors} userInfo={userInfo} schoolInfo={schoolInfo} rolePages={rolePages} setRolePages={setRolePages} handleMoveToNextScreen={handleMoveToNextScreen}/>;
						case 'edit_constraints':
							return <EditConstraints isLoading={isLoading} subscreenRestricted={subscreenRestricted} subscreenLocked={subscreenLocked} subscreenResponsibleRoleID={subscreenResponsibleRoleID} supervisors={supervisors} userIsSupervisor={userIsSupervisor} userIsAdmin={current_user_is_admin} openSupervisorsList={openSupervisorsList} closeSupervisorsList={closeSupervisorsList} addSupervisorToDepartment={addSupervisorToDepartment} removeSupervisorFromDepartment={removeSupervisorFromDepartment} subscreen={subscreen} departments={departments} setDepartments={setDepartments} departmentID={departmentID} sortedDepartments={sorted_departments} changeDepartment={changeDepartment} userInfo={userInfo} schoolInfo={schoolInfo} schoolType={schoolType} scheduleInfoLoading={scheduleInfoLoading} scheduleInfo={scheduleInfo} scheduleType={scheduleType} periods={periods} scheduleStructureDataLoading={scheduleStructureDataLoading} scheduleStructureData={scheduleStructureData} usersLoading={usersLoading} users={users} appSubscreens={appSubscreens} setNotifications={setNotifications} rolePages={rolePages} setRolePages={setRolePages} teachersLoading={teachersLoading} coursesLoading={coursesLoading} classroomsLoading={classroomsLoading} teachers={teachers} courses={courses} classrooms={classrooms} students={students} setTeachers={setTeachers} setCourses={setCourses} setClassrooms={setClassrooms} setStudents={setStudents} sections={sections} setSections={setSections} labels={labels} subscreenSelect={subscreenSelect} submitDepartment={submitDepartment} reviewing={reviewing} showNextButton={childShowNextButton} handleMarkCurrentSubscreenComplete={handleMarkCurrentSubscreenComplete} handleMoveToNextScreen={handleMoveToNextScreen} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo}/>;
						case 'section_creation':
							return <SectionCreation isLoading={isLoading} setIsLoading={setIsLoading} schoolSubscribed={schoolSubscribed} subscreenRestricted={subscreenRestricted} subscreenLocked={subscreenLocked} subscreenResponsibleRoleID={subscreenResponsibleRoleID} getSubscreenDepartmentProgress={getSubscreenDepartmentProgress} getNewData={handlePullNewData} subscreen={subscreen} departments={departments} departmentsLoading={departmentsLoading} departmentID={departmentID} sortedDepartments={sorted_departments} changeDepartment={changeDepartment} setDepartments={setDepartments} userInfo={userInfo} schoolInfo={schoolInfo} schoolType={schoolType} scheduleInfoLoading={scheduleInfoLoading} scheduleInfo={scheduleInfo} scheduleStructureData={scheduleStructureData} teachersLoading={teachersLoading} teachers={teachers} setTeachers={setTeachers} coursesLoading={coursesLoading} courses={courses} setCourses={setCourses} studentsLoading={studentsLoading} students={students} setStudents={setStudents} classrooms={classrooms} sectionsLoading={sectionsLoading} sections={sections} setSections={setSections} subsections={subsections} setSubsections={setSubsections} labels={labels} setLabels={setLabels} usersLoading={usersLoading} users={users} userIsAdmin={current_user_is_admin} userIsSupervisor={userIsSupervisor} supervisors={supervisors} closeSupervisorsList={closeSupervisorsList} openSupervisorsList={openSupervisorsList} addSupervisorToDepartment={addSupervisorToDepartment} removeSupervisorFromDepartment={removeSupervisorFromDepartment} subscreenSelect={subscreenSelect} submitDepartment={submitDepartment} reviewing={reviewing} resetProgress={resetProgress} changesSavedMessage={changesSavedMessage} setChangesSavedMessage={setChangesSavedMessage} setRolePages={setRolePages} showNextButton={childShowNextButton} handleMoveToNextScreen={handleMoveToNextScreen} nextStepButtonText={nextStepButtonText} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo}/>;
						case 'manage_labels':
							return <ManageLabels isLoading={isLoading} setIsLoading={setIsLoading} subscreenRestricted={subscreenRestricted} subscreenLocked={subscreenLocked} getNewData={handlePullNewData} subscreen={subscreen} departments={departments} userInfo={userInfo} schoolInfo={schoolInfo} schoolType={schoolType} scheduleInfo={scheduleInfo} teachersLoading={teachersLoading} teachers={teachers} coursesLoading={coursesLoading} courses={courses} setCourses={setCourses} studentsLoading={studentsLoading} students={students} setStudents={setStudents} sectionsLoading={sectionsLoading} sections={sections} setSections={setSections} subsections={subsections} labels={labels} setLabels={setLabels} handleMoveToNextScreen={handleMoveToNextScreen} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo}/>;
						case 'place_sections':
							return <PlaceSectionsScreen isLoading={isLoading} setIsLoading={setIsLoading} permissionChecked={permissionChecked} schoolSubscribed={schoolSubscribed} subscreenRestricted={subscreenRestricted} subscreenLocked={subscreenLocked} subscreenResponsibleRoleID={subscreenResponsibleRoleID} subscreen={subscreen} getNewData={handlePullNewData} departmentsLoading={departmentsLoading} departments={departments} setDepartments={setDepartments} departmentID={departmentID} sortedDepartments={sorted_departments} changeDepartment={changeDepartment} userInfo={userInfo} schoolInfo={schoolInfo} schoolType={schoolType} schoolInfoLoading={schoolInfoLoading} setSchoolInfoLoading={setSchoolInfoLoading} scheduleInfo={scheduleInfo} scheduleInfoLoading={scheduleInfoLoading} setScheduleInfo={setScheduleInfo} scheduleStructureData={scheduleStructureData} setScheduleStructureData={setScheduleStructureData} scheduleStructureDataLoading={scheduleStructureDataLoading} lunchInfo={lunchInfo} coursesLoading={coursesLoading} courses={courses} setCourses={setCourses} teachersLoading={teachersLoading} teachers={teachers} setTeachers={setTeachers} classroomsLoading={classroomsLoading} classrooms={classrooms} setClassrooms={setClassrooms} studentsLoading={studentsLoading} students={students} setStudents={setStudents} sectionsLoading={sectionsLoading} sections={sections} setSections={setSections} subsections={subsections} setSubsections={setSubsections} labels={labels} setLabels={setLabels} reviewing={reviewing} resetProgress={resetProgress} users={users} userIsAdmin={current_user_is_admin} userIsSupervisor={userIsSupervisor} supervisors={supervisors} openSupervisorsList={openSupervisorsList} closeSupervisorsList={closeSupervisorsList} addSupervisorToDepartment={addSupervisorToDepartment} removeSupervisorFromDepartment={removeSupervisorFromDepartment} subscreenSelect={subscreenSelect} submitDepartment={submitDepartment} changesSavedMessage={changesSavedMessage} setChangesSavedMessage={setChangesSavedMessage} showNextButton={childShowNextButton} appSubscreens={appSubscreens} rolePages={rolePages} handleMoveToNextScreen={handleMoveToNextScreen} setLoadScreen={setLoadScreen} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} setUniversalSidebarLoadingInfo={setUniversalSidebarLoadingInfo}/>;
						case 'place_students':
							return <PlaceStudentsScreen schoolSubscribed={schoolSubscribed} subscreenRestricted={subscreenRestricted} subscreenLocked={subscreenLocked} subscreen={subscreen} isLoading={isLoading} setIsLoading={setIsLoading} schoolInfo={schoolInfo} schoolType={schoolType} scheduleInfo={scheduleInfo} userInfo={userInfo} departmentsLoading={departmentsLoading} departments={departments} appSubscreens={appSubscreens} rolePages={rolePages} subscreenSelect={subscreenSelect} getNewData={handlePullNewData} showNextButton={childShowNextButton} handleMoveToNextScreen={handleMoveToNextScreen} setLoadScreen={setLoadScreen}/>;
						default:
							return (
								<Dashboard match={match} userInfo={userInfo} schoolInfo={schoolInfo} scheduleStructureData={scheduleStructureData} lunchInfo={lunchInfo} goToScreen={goToScreen} rolePages={rolePages} lastScreen={last_screen} studentsLoading={studentsLoading} students={students} studentMatchPercentages={studentMatchPercentages} classroomsLoading={classroomsLoading} classrooms={classrooms} teachersLoading={teachersLoading} teachers={teachers} coursesLoading={coursesLoading} courses={courses} sections={sections}/>
							);
					}
				})()}
			</div>
		</div>
	);
}
